import React, { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useLocation } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import {
    renderDatasetSelectorListItemSpan,
    renderDatasetSelectorSpan,
    DatasetUnion,
    formatDatasetDates,
    formatLegacyDatasetDates,
    sortDatasetByDatesDesc,
} from '@components-clean/navbar/dataset-selector.utils';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useLoading } from '@contexts-clean/loader-context';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { NullableStudy } from '@contexts/local-state-context';
import { useAuthorization, useLocalState, useLogger } from '@hooks/index';
import { ExtendedDatasetFieldsWithIcon, getDatasetDates } from '@utils/cm-observations.service';
import { loadDataset } from '@utils/study-data.service';
import { GET_DATASET_REVIEWS, GET_DATASETS } from '@queries/central-monitoring';
import {
    dataSets,
    dataSetsVariables,
    dataSetReviews,
    dataSetReviewsVariables,
} from '@queries/generated/central-monitoring.generated';
import { isCM2Page, isLegacyCMPage } from '@config/routes/page-data-helpers';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

import './dataset-selector.scss';

interface DatasetSelectorProps {
    disabled?: boolean;
    isOrgUser: boolean;
    study: NullableStudy | undefined;
    hideBorder: boolean;
    orgHasNoStudies: boolean;
    datasetId: string | null | undefined;
    selectDatasetId: (newId: string | null | undefined) => void;
    datasetCutDate: string | null | undefined;
    currentDataset: DatasetUnion | null;
    setCurrentDataset: Dispatch<SetStateAction<DatasetUnion | null>>;
    datasets: DatasetUnion[];
    setDatasets: Dispatch<SetStateAction<DatasetUnion[]>>;
    clearDataset: () => void;
    onChangeCallback?: () => void;
}

const DatasetSelector = ({
    disabled,
    isOrgUser,
    study,
    hideBorder,
    orgHasNoStudies,
    datasetId,
    selectDatasetId,
    datasetCutDate,
    currentDataset,
    setCurrentDataset,
    datasets,
    setDatasets,
    clearDataset,
    onChangeCallback,
}: DatasetSelectorProps) => {
    const location = useLocation();
    const { showLoading, hideLoading } = useLoading();
    const user = useAuthorization();
    const Logger = useLogger();
    const isLegacyCM = isLegacyCMPage(location.pathname);
    const isCM2 = isCM2Page(location.pathname);
    const { state, dispatch } = useLocalState();
    const studyId = (study?.id ?? '') as string;
    const [viewSiteDashboardTrigger, setViewSiteDashboardTrigger] = useState<number>(0);

    // DATASET SELECTION
    const selectLegacyDataset = (legacyDataset: ExtendedDatasetFieldsWithIcon | null) => {
        selectDatasetId(legacyDataset?.id as string);
        setCurrentDataset(legacyDataset);

        dispatch({
            type: 'SET_SELECTED_DATE',
            datasetDate: legacyDataset,
        });

        // For legacy CM, processed and scoring data is loaded into localState as json, via below method
        if (study) {
            loadDataset(
                dispatch,
                user,
                study,
                (legacyDataset?.isCMR ? legacyDataset?.dataSetId : legacyDataset?.id) as string,
                state,
            )
                ?.then(() => {
                    Logger.log('Loaded dataset data: ', legacyDataset?.id);
                })
                .catch((e) => {
                    Logger.log('Error loading dataset data: ', e);
                });
        }
    };

    const selectCM2Dataset = (cm2dataset: CM2Dataset | null) => {
        selectDatasetId(cm2dataset?.id?.toString());
        setCurrentDataset(cm2dataset);

        dispatch({
            type: 'SET_SELECTED_CM2_DATASET',
            selectedCM2Dataset: cm2dataset,
        });
    };

    // FETCH DATASETS
    const [getDataSets, { data: legacyDataSetsData, loading: loadingLegacyDatasets, called: calledLegacyDatasets }] =
        useLazyQuery<dataSets, dataSetsVariables>(GET_DATASETS);
    const [
        getDataSetsReview,
        { data: legacyDataSetReviewsData, loading: loadingDatasetsReviews, called: calledDatasetsReviews },
    ] = useLazyQuery<dataSetReviews, dataSetReviewsVariables>(GET_DATASET_REVIEWS);

    useEffect(() => {
        if (isLegacyCM && studyId) {
            getDataSets({ variables: { studyId } }).catch((err) => window.console.error(err));
            getDataSetsReview({ variables: { studyId } }).catch((err) => window.console.error(err));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isLegacyCM, studyId]);

    const {
        data: cm2DataSetsData,
        isValidating: validatingCM2Data,
        isLoading: loadingCM2Data,
    } = DatasetApi.useGetAll(studyId, !isCM2);

    const loading =
        loadingLegacyDatasets ||
        loadingDatasetsReviews ||
        loadingCM2Data ||
        (isLegacyCM && studyId && (!calledLegacyDatasets || !calledDatasetsReviews));

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [loading]);

    // DATASETS SORTING, FILTERING AND FORMATTING
    const loadCM2Datasets = () => {
        if (cm2DataSetsData === undefined) return;

        if (!cm2DataSetsData || cm2DataSetsData.length === 0) {
            clearDataset();
            return;
        }

        const cm2DataSets = cm2DataSetsData?.filter(Boolean) ?? [];
        const sortedCM2DataSets =
            cm2DataSets
                .filter((d) => !!d && (isOrgUser ? d.active : true))
                .map(formatDatasetDates)
                .sort(sortDatasetByDatesDesc) ?? [];

        setDatasets(sortedCM2DataSets);
        if (datasetCutDate)
            selectCM2Dataset(
                sortedCM2DataSets.find((d) => d?.dataCutDate === datasetCutDate) ?? sortedCM2DataSets.at(0) ?? null,
            );
        else
            selectCM2Dataset(
                sortedCM2DataSets.find((d) => d?.id === Number(datasetId)) ?? sortedCM2DataSets.at(0) ?? null,
            );
    };

    const loadLegacyDatasets = () => {
        if (!legacyDataSetsData || !legacyDataSetReviewsData) return;

        const legacyDataSets = getDatasetDates(
            legacyDataSetsData || null,
            legacyDataSetReviewsData || null,
            user?.systemAdminLevel ?? 0,
        );
        const sortedLegacyDataSets =
            legacyDataSets
                .filter((d) => !!d && (isOrgUser ? d.active : true))
                .map(formatLegacyDatasetDates)
                .sort(sortDatasetByDatesDesc) ?? [];

        setDatasets(sortedLegacyDataSets);

        const defaultDataset = sortedLegacyDataSets.at(0) ?? null;
        if (datasetCutDate)
            selectLegacyDataset(sortedLegacyDataSets.find((d) => d?.dataCutDate === datasetCutDate) ?? defaultDataset);
        else {
            selectLegacyDataset(sortedLegacyDataSets.find((d) => d?.id === datasetId) ?? defaultDataset);
        }
    };

    useEffect(() => {
        if (isLegacyCM) {
            loadLegacyDatasets();
        } else if (isCM2) {
            loadCM2Datasets();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isLegacyCM, isCM2, legacyDataSetsData, legacyDataSetReviewsData, cm2DataSetsData]);

    useEffect(() => {
        const viewLegacySiteDashboardSubscription = NavigationService.onViewLegacySiteDashboard().subscribe(() => {
            setViewSiteDashboardTrigger((prev) => prev + 1);
        });

        return () => viewLegacySiteDashboardSubscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (isLegacyCM && viewSiteDashboardTrigger > 0)
            selectLegacyDataset((datasets.at(0) as ExtendedDatasetFieldsWithIcon) ?? null);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [viewSiteDashboardTrigger]);

    useEffect(() => {
        if (orgHasNoStudies) {
            setDatasets([]);
            selectCM2Dataset(null);
            selectLegacyDataset(null);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [orgHasNoStudies]);

    return (
        <div
            className={cn('k-d-flex k-align-items-center k-w-full dataset-selector')}
            style={{
                columnGap: '10px',
                maxWidth: '208px',
            }}
        >
            <FontAwesomeIcon icon={['far', 'database']} size="xl" className="k-color-white" />
            <BasicDropDown
                className="k-h-8 k-w-full navbar-dropdown"
                data-testid="select-dataset-in-focus"
                data={datasets}
                dataItemKey="id"
                disabled={disabled}
                readOnly={IsPopOut()}
                loading={!disabled && (loading || validatingCM2Data)}
                textField={isLegacyCM ? '_dataCutDate' : 'dataCutDate'}
                value={currentDataset}
                onChange={(e) => {
                    if (isLegacyCM) {
                        const selected = e.value as ExtendedDatasetFieldsWithIcon;
                        selectLegacyDataset(selected);
                    } else {
                        const selected = e.value as CM2Dataset;
                        selectCM2Dataset(selected);
                    }
                    // May need extra side effects after selecting a new dataset.
                    if (onChangeCallback) {
                        onChangeCallback();
                    }
                }}
                hideBorder={hideBorder}
                customClassName="navbar-popup dataset-selector-popup"
                valueRender={renderDatasetSelectorSpan}
                itemRender={renderDatasetSelectorListItemSpan}
                filterable
            />
        </div>
    );
};

export default DatasetSelector;
